import React from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import {
  CloudDownloadOutlined as DownloadIcon,
  CloudDoneOutlined as SavedIcon,
  CloudOffOutlined as OfflineIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../constants/documentTheme';
import DocumentProBadge from './DocumentProBadge';

/**
 * The offline-access surface for a trip: one strip above the itinerary that says
 * whether this trip is readable without a signal, and lets the traveller change
 * that.
 *
 * It is presentational. The snapshot itself is owned by `useOfflineTrip` on the
 * page, because the page also needs it to render the itinerary when a request
 * fails, and two copies of that state would drift.
 */
const OfflineItineraryBar = ({
  supported = true,
  isOnline = true,
  isSaved = false,
  savedAt = null,
  saving = false,
  error = null,
  hasTripPass = true,
  onSave,
  onRemove,
  onRequireTripPass
}) => {
  const { t } = useTranslation();

  // A browser with no IndexedDB cannot honour the promise, and a bar offering
  // something that will not work is worse than no bar.
  if (!supported) return null;

  const savedAgo = savedAt
    ? formatDistanceToNow(new Date(savedAt), { addSuffix: true })
    : null;

  const handleSave = () => {
    if (!hasTripPass) {
      onRequireTripPass?.();
      return;
    }
    onSave?.();
  };

  let icon = <DownloadIcon sx={{ fontSize: 20, color: DOCUMENT.ink3 }} />;
  let heading = t('offline.saveTitle');
  let detail = t('offline.saveHint');

  if (!isOnline) {
    icon = <OfflineIcon sx={{ fontSize: 20, color: DOCUMENT.accent }} />;
    heading = t('offline.offlineTitle');
    detail = isSaved
      ? t('offline.offlineSavedHint', { when: savedAgo })
      : t('offline.offlineUnsavedHint');
  } else if (isSaved) {
    icon = <SavedIcon sx={{ fontSize: 20, color: DOCUMENT.accent }} />;
    heading = t('offline.savedTitle');
    detail = t('offline.savedHint', { when: savedAgo });
  }

  if (error) {
    detail = error === 'offline' ? t('offline.errorOffline') : t('offline.errorFailed');
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1.5, sm: 2 },
        px: { xs: 2, sm: 2.5 },
        py: 1.75,
        mb: 3,
        bgcolor: DOCUMENT.surface,
        border: `1px solid ${DOCUMENT.cardRule}`,
        borderRadius: '2px'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
        {icon}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: DOCUMENT.sans,
              fontSize: '0.8125rem',
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: DOCUMENT.ink
            }}
          >
            {heading}
          </Typography>
          <Typography
            sx={{
              fontFamily: DOCUMENT.sans,
              fontSize: '0.8125rem',
              color: error ? DOCUMENT.oxblood : DOCUMENT.ink3,
              mt: 0.25
            }}
          >
            {detail}
          </Typography>
        </Box>
      </Box>

      {/* Saving needs the network, so offline the only sensible action is none. */}
      {isOnline && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {isSaved && (
            <Button
              size="small"
              onClick={onRemove}
              disabled={saving}
              sx={{
                fontFamily: DOCUMENT.sans,
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: DOCUMENT.ink3
              }}
            >
              {t('offline.remove')}
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{
              fontFamily: DOCUMENT.sans,
              fontSize: '0.75rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              borderColor: DOCUMENT.accent,
              color: DOCUMENT.accent,
              '&:hover': { borderColor: DOCUMENT.accent, bgcolor: 'rgba(138,106,40,0.06)' }
            }}
          >
            {saving
              ? t('offline.saving')
              : isSaved
                ? t('offline.update')
                : t('offline.save')}
          </Button>
          {!hasTripPass && <DocumentProBadge variant="pill" />}
        </Box>
      )}
    </Box>
  );
};

export default OfflineItineraryBar;
